import { useState } from 'react';
import { Menu, Box, Typography, MenuItem, Chip, ListItem, Tooltip, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import AppsIcon from '@mui/icons-material/Apps';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EventIcon from '@mui/icons-material/Event';
import SchoolIcon from '@mui/icons-material/School';
import BadgeIcon from '@mui/icons-material/Badge';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getToken } from '@repo/api-client';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

// src/AppSwitcher/AppSwitcher.tsx
var APP_ITEMS = [
  {
    key: "auth",
    label: "Auth",
    shortLabel: "AUTH",
    description: "User Management & Profile",
    icon: /* @__PURE__ */ jsx(AdminPanelSettingsIcon, { sx: { fontSize: 20 } }),
    iconBg: "#eff6ff",
    iconColor: "#2563eb"
  },
  {
    key: "events",
    label: "Events",
    shortLabel: "EVENTS",
    description: "Conferences & Accommodations",
    icon: /* @__PURE__ */ jsx(EventIcon, { sx: { fontSize: 20 } }),
    iconBg: "#ecfdf5",
    iconColor: "#059669"
  },
  {
    key: "wfl",
    label: "WFL",
    shortLabel: "WFL",
    description: "Word For Life Classes & Handbooks",
    icon: /* @__PURE__ */ jsx(SchoolIcon, { sx: { fontSize: 20 } }),
    iconBg: "#fffbeb",
    iconColor: "#d97706"
  },
  {
    key: "member",
    label: "Membership",
    shortLabel: "MEMBER",
    description: "Member Roster, Org Tree & Transfers",
    icon: /* @__PURE__ */ jsx(BadgeIcon, { sx: { fontSize: 20 } }),
    iconBg: "#f5f3ff",
    iconColor: "#7c3aed"
  }
];
var resolveAppUrl = (appKey, appUrls) => {
  if (appUrls?.[appKey]) {
    return appUrls[appKey];
  }
  if (typeof import.meta !== "undefined" && import.meta.env) {
    const envKey = `VITE_${appKey.toUpperCase()}_URL`;
    const envVal = import.meta.env[envKey];
    if (envVal) return envVal;
  }
  if (typeof window !== "undefined") {
    const { hostname, protocol } = window.location;
    const isLocal = hostname === "localhost" || hostname === "127.0.0.1";
    if (isLocal) {
      const portMap = {
        auth: 4001,
        events: 3010,
        wfl: 3012,
        member: 3014
      };
      return `http://${hostname}:${portMap[appKey]}`;
    }
    const isStaging = hostname.includes("staging") || hostname.includes("sit");
    const parts = hostname.split(".");
    const baseDomain = parts.length >= 2 ? parts.slice(-2).join(".") : hostname;
    if (isStaging) {
      return `${protocol}//staging-${appKey}.${baseDomain}`;
    }
    return `${protocol}//${appKey}.${baseDomain}`;
  }
  return `https://${appKey}.jabeztech.com`;
};
var detectCurrentApp = (propCurrentApp) => {
  if (propCurrentApp) return propCurrentApp;
  if (typeof window === "undefined") return null;
  const { hostname, port } = window.location;
  if (port === "4001" || hostname.startsWith("auth.") || hostname.includes("-auth.")) {
    return "auth";
  }
  if (port === "3010" || hostname.startsWith("events.") || hostname.includes("-events.")) {
    return "events";
  }
  if (port === "3012" || hostname.startsWith("wfl.") || hostname.includes("-wfl.")) {
    return "wfl";
  }
  if (port === "3014" || hostname.startsWith("member.") || hostname.includes("-member.")) {
    return "member";
  }
  return null;
};
var AppSwitcher = ({
  variant = "sidebar",
  isWide = true,
  currentApp: propCurrentApp,
  appUrls,
  sx,
  className,
  onNavigate
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const activeApp = detectCurrentApp(propCurrentApp);
  const activeConfig = APP_ITEMS.find((item) => item.key === activeApp);
  const handleAppSelect = (targetApp) => {
    setAnchorEl(null);
    const baseUrl = resolveAppUrl(targetApp, appUrls);
    if (targetApp === activeApp) {
      return;
    }
    if (onNavigate) {
      onNavigate(targetApp, baseUrl);
      return;
    }
    if (typeof window !== "undefined") {
      try {
        const url = new URL(baseUrl, window.location.origin);
        const token = getToken();
        if (token) {
          url.searchParams.set("token", token);
        }
        const currentLng = typeof document !== "undefined" && document.documentElement.lang || localStorage.getItem("i18nextLng") || "";
        if (currentLng) {
          url.searchParams.set("lng", currentLng);
        }
        window.location.href = url.toString();
      } catch {
        window.location.href = baseUrl;
      }
    }
  };
  const menuContent = /* @__PURE__ */ jsxs(
    Menu,
    {
      anchorEl,
      open: openMenu,
      onClose: () => setAnchorEl(null),
      anchorOrigin: {
        vertical: "center",
        horizontal: "right"
      },
      transformOrigin: {
        vertical: "center",
        horizontal: "left"
      },
      slotProps: {
        paper: {
          sx: {
            minWidth: 260,
            boxShadow: 4,
            borderRadius: 2.5,
            overflow: "hidden",
            p: 0,
            ml: 1
          }
        }
      },
      children: [
        /* @__PURE__ */ jsxs(Box, { sx: { px: 2, py: 1.25, bgcolor: "#f8fafc", borderBottom: "1px solid rgba(0,0,0,0.08)" }, children: [
          /* @__PURE__ */ jsx(Typography, { variant: "subtitle2", sx: { fontWeight: 700, fontSize: "0.85rem", color: "text.primary" }, children: "Applications" }),
          /* @__PURE__ */ jsx(Typography, { variant: "caption", sx: { color: "text.secondary", display: "block", fontSize: "0.75rem" }, children: "Navigate between Acts Church apps" })
        ] }),
        /* @__PURE__ */ jsx(Box, { sx: { py: 0.5 }, children: APP_ITEMS.map((app) => {
          const isCurrent = app.key === activeApp;
          return /* @__PURE__ */ jsxs(
            MenuItem,
            {
              selected: isCurrent,
              onClick: () => handleAppSelect(app.key),
              sx: {
                py: 1.2,
                px: 2,
                gap: 1.5,
                display: "flex",
                alignItems: "center",
                transition: "background-color 0.15s ease",
                "&.Mui-selected": {
                  bgcolor: "rgba(25, 118, 210, 0.08)",
                  "&:hover": {
                    bgcolor: "rgba(25, 118, 210, 0.12)"
                  }
                }
              },
              children: [
                /* @__PURE__ */ jsx(
                  Box,
                  {
                    sx: {
                      width: 34,
                      height: 34,
                      borderRadius: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: app.iconBg,
                      color: app.iconColor,
                      flexShrink: 0
                    },
                    children: app.icon
                  }
                ),
                /* @__PURE__ */ jsxs(Box, { sx: { flexGrow: 1, minWidth: 120 }, children: [
                  /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
                    /* @__PURE__ */ jsx(Typography, { variant: "body2", sx: { fontWeight: 600, fontSize: "0.875rem" }, children: app.label }),
                    isCurrent && /* @__PURE__ */ jsx(
                      Chip,
                      {
                        label: "Active",
                        size: "small",
                        color: "primary",
                        variant: "outlined",
                        sx: { height: 18, fontSize: "0.65rem", fontWeight: 700, px: 0.25 }
                      }
                    )
                  ] }),
                  /* @__PURE__ */ jsx(
                    Typography,
                    {
                      variant: "caption",
                      sx: { color: "text.secondary", display: "block", fontSize: "0.72rem", mt: 0.25 },
                      children: app.description
                    }
                  )
                ] }),
                !isCurrent && /* @__PURE__ */ jsx(OpenInNewIcon, { sx: { fontSize: 15, color: "text.disabled", ml: 0.5 } })
              ]
            },
            app.key
          );
        }) })
      ]
    }
  );
  if (variant === "sidebar") {
    const collapsedButtonSx = {
      p: 1,
      justifyContent: isWide ? "initial" : "center"
    };
    const collapsedIconSx = {
      minWidth: isWide ? 56 : 0,
      justifyContent: isWide ? "initial" : "center",
      alignItems: "center",
      display: "flex"
    };
    return /* @__PURE__ */ jsxs(ListItem, { disablePadding: true, sx: { width: "100%", ...sx }, className, children: [
      /* @__PURE__ */ jsx(
        Tooltip,
        {
          title: !isWide ? `App: ${activeConfig ? activeConfig.label : "Switch App"}` : "",
          placement: "right",
          children: /* @__PURE__ */ jsxs(
            ListItemButton,
            {
              onClick: (e) => setAnchorEl(e.currentTarget),
              sx: collapsedButtonSx,
              "aria-haspopup": "true",
              "aria-expanded": openMenu ? "true" : void 0,
              children: [
                /* @__PURE__ */ jsx(ListItemIcon, { sx: collapsedIconSx, children: /* @__PURE__ */ jsx(AppsIcon, {}) }),
                /* @__PURE__ */ jsx(
                  ListItemText,
                  {
                    primary: activeConfig ? activeConfig.label : "Apps",
                    secondary: "Switch App",
                    secondaryTypographyProps: { fontSize: "0.75rem", color: "text.secondary" },
                    sx: { display: isWide ? "block" : "none", my: 0 }
                  }
                ),
                isWide && /* @__PURE__ */ jsx(
                  ExpandMoreIcon,
                  {
                    fontSize: "small",
                    sx: {
                      ml: "auto",
                      color: "action.active",
                      transform: openMenu ? "rotate(180deg)" : "none",
                      transition: "transform 0.2s ease"
                    }
                  }
                )
              ]
            }
          )
        }
      ),
      menuContent
    ] });
  }
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Tooltip, { title: `App: ${activeConfig ? activeConfig.label : "Switch App"}`, placement: "top", children: /* @__PURE__ */ jsxs(
      Box,
      {
        component: "button",
        type: "button",
        onClick: (e) => setAnchorEl(e.currentTarget),
        className,
        "aria-haspopup": "true",
        "aria-expanded": openMenu ? "true" : void 0,
        sx: {
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          px: isWide ? 1.5 : 1,
          py: 0.75,
          minHeight: 36,
          minWidth: isWide ? void 0 : 36,
          cursor: "pointer",
          borderRadius: 2,
          bgcolor: "white",
          border: "1px solid rgba(0, 0, 0, 0.1)",
          boxShadow: 2,
          outline: "none",
          color: "text.primary",
          userSelect: "none",
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: 4,
            bgcolor: "#f8fafc"
          },
          ...sx
        },
        children: [
          /* @__PURE__ */ jsx(AppsIcon, { sx: { fontSize: 20, color: "text.secondary" } }),
          isWide && /* @__PURE__ */ jsx(Typography, { variant: "body2", sx: { fontWeight: 600, fontSize: "0.875rem" }, children: activeConfig ? activeConfig.label : "Apps" })
        ]
      }
    ) }),
    menuContent
  ] });
};
var AppSwitcher_default = AppSwitcher;

export { APP_ITEMS, AppSwitcher, AppSwitcher_default, detectCurrentApp, resolveAppUrl };
//# sourceMappingURL=chunk-WN3RTF5O.mjs.map
//# sourceMappingURL=chunk-WN3RTF5O.mjs.map